/**
 * Memória da última loja visitada em hosts white-label.
 *
 * Em domínios do fornecedor (ex.: amecardapio.com, domínio próprio) o usuário
 * NUNCA pode cair em conteúdo institucional da AmeUp. Guardamos o slug da
 * última loja acessada — e o caminho completo válido — para conseguir voltar
 * para o lugar certo em rotas genéricas (raiz, 404, erros).
 */

import { isWhiteLabelHost } from '@/utils/appUrl';

const STORAGE_KEY = 'wl_last_store_slug';
const PATH_STORAGE_KEY = 'wl_last_store_path';

/** Salva o slug da loja atual (silencioso em navegadores sem storage). */
export const rememberStoreSlug = (slug?: string | null): void => {
  if (!slug) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, slug);
  } catch {
    /* storage indisponível: seguimos sem memória */
  }
};

/** Recupera o slug da última loja visitada, se existir. */
export const getLastStoreSlug = (): string | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
};

/**
 * Salva o último caminho de loja válido (ex.: "/loja/chef-eventos?date=...").
 * Só aceita caminhos relativos para evitar open redirect.
 */
export const rememberStorePath = (pathAndQuery?: string | null): void => {
  if (!pathAndQuery) return;
  if (!pathAndQuery.startsWith('/') || pathAndQuery.startsWith('//')) return;
  try {
    window.localStorage.setItem(PATH_STORAGE_KEY, pathAndQuery);
  } catch {
    /* storage indisponível */
  }
};

/** Recupera o último caminho de loja válido, se existir. */
export const getLastStorePath = (): string | null => {
  try {
    const value = window.localStorage.getItem(PATH_STORAGE_KEY);
    if (!value || !value.trim()) return null;
    if (!value.startsWith('/') || value.startsWith('//')) return null;
    return value;
  } catch {
    return null;
  }
};

/**
 * Caminho seguro para "voltar ao início".
 *
 * Em hosts white-label nunca podemos cair na home institucional da AmeUp, então
 * usamos o último caminho de loja conhecido. Em hosts canônicos, "/" continua
 * sendo o destino correto.
 */
export const getHomeFallbackPath = (): string => {
  if (!isWhiteLabelHost()) return '/';
  const path = getLastStorePath();
  if (path) return path;
  const slug = getLastStoreSlug();
  if (slug) return `/loja/${slug}`;
  return '/';
};

